import {
    ATEMOYA_CHEATS_ALLOWED_STORAGE_KEY,
    GAME_LAUNCH_CHEATS_ENABLED_PARAM,
    GAME_LAUNCH_OPEN_CONTEXT_PARAM,
    GAME_LAUNCH_PARENT_ORIGIN_PARAM,
    GAME_LAUNCH_RETURN_URL_PARAM,
    GameLaunchMode,
    SUPPORTED_GAME_LAUNCH_MODES,
    type LaunchMode,
} from '../constants/gameLaunchMode';
import { Routes } from '../routes/routes';

export function buildAccessLinkPath(accessHash: string): string {
    return `/${Routes.ACCESS}/${accessHash}`;
}

export function gameClientPath(gameId: string, relativePath: string): string {
    return `/games/${gameId}/client/dist/${relativePath}`;
}

function isMobileLikeDevice(): boolean {
    const hasCoarsePointer =
        typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
    const hasNoHover =
        typeof window.matchMedia === 'function' && window.matchMedia('(hover: none)').matches;
    return hasCoarsePointer || hasNoHover;
}

export function getGameLaunchMode(): LaunchMode {
    const configuredMode =
        typeof window.ATEMOYA_GAME_LAUNCH_MODE === 'string'
            ? window.ATEMOYA_GAME_LAUNCH_MODE.trim().toLowerCase()
            : '';

    if (SUPPORTED_GAME_LAUNCH_MODES.has(configuredMode)) {
        if (configuredMode === GameLaunchMode.Auto) {
            return isMobileLikeDevice() ? GameLaunchMode.SameTab : GameLaunchMode.Modal;
        }
        return configuredMode as LaunchMode;
    }

    return isMobileLikeDevice() ? GameLaunchMode.SameTab : GameLaunchMode.Modal;
}

export function setAtemoyaCheatsAllowed(cheatsAllowed: boolean): void {
    window.ATEMOYA_CHEATS_ALLOWED = cheatsAllowed;
    try {
        window.sessionStorage.setItem(
            ATEMOYA_CHEATS_ALLOWED_STORAGE_KEY,
            cheatsAllowed ? '1' : '0'
        );
    } catch {
        // ignore storage errors (private mode/restrictions)
    }
}

export function withGameLaunchParams(
    gameUrl: string,
    params: { locale: string; currency: string; cheatsEnabled?: boolean }
): string {
    try {
        const url = new URL(gameUrl, window.location.origin);
        url.searchParams.set('currency', params.currency);
        url.searchParams.set('locale', params.locale);
        if (typeof params.cheatsEnabled === 'boolean') {
            url.searchParams.set(
                GAME_LAUNCH_CHEATS_ENABLED_PARAM,
                params.cheatsEnabled ? '1' : '0'
            );
        }
        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return gameUrl;
    }
}

export function buildPlayPageUrl(gameUrl: string, gameName: string): string {
    const url = new URL(`/${Routes.PLAY}`, window.location.origin);
    url.searchParams.set('gameUrl', gameUrl);
    url.searchParams.set('gameName', gameName);
    return `${url.pathname}${url.search}`;
}

export function withLauncherContext(
    gameUrl: string,
    launchMode: LaunchMode,
    returnUrl?: string
): string {
    const resolvedReturnUrl = returnUrl
        ? new URL(returnUrl, window.location.origin).href
        : window.location.href;

    try {
        window.sessionStorage.setItem(GAME_LAUNCH_RETURN_URL_PARAM, resolvedReturnUrl);
    } catch {
        // ignore storage errors (private mode/restrictions)
    }

    try {
        const url = new URL(gameUrl, window.location.origin);
        if (!url.searchParams.has(GAME_LAUNCH_RETURN_URL_PARAM)) {
            url.searchParams.set(GAME_LAUNCH_RETURN_URL_PARAM, resolvedReturnUrl);
        }
        if (!url.searchParams.has(GAME_LAUNCH_OPEN_CONTEXT_PARAM)) {
            if (launchMode === GameLaunchMode.NewTab) {
                url.searchParams.set(GAME_LAUNCH_OPEN_CONTEXT_PARAM, GameLaunchMode.NewTab);
            } else if (launchMode === GameLaunchMode.SameTab) {
                url.searchParams.set(GAME_LAUNCH_OPEN_CONTEXT_PARAM, GameLaunchMode.SameTab);
            } else {
                url.searchParams.set(GAME_LAUNCH_OPEN_CONTEXT_PARAM, GameLaunchMode.Modal);
            }
        }
        if (!url.searchParams.has(GAME_LAUNCH_PARENT_ORIGIN_PARAM)) {
            url.searchParams.set(GAME_LAUNCH_PARENT_ORIGIN_PARAM, window.location.origin);
        }
        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return gameUrl;
    }
}
